import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus } from 'lucide-react';
import { Botao, Cartao } from '../../componentes/base';
import { Erro } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { api, get, getTodos } from '../../nucleo/api';
import { dinheiro, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { Ficha } from './tipos';
import { rotuloForma, rotuloNomeForma } from '../../nucleo/forma-pagamento';

/** Fase 2 (W25, K14): condições de compra do fornecedor (`GET`/`PUT /cadastros/parceiros/:id/fornecimento`). */
export interface Fornecimento {
  parceiro_id: string;
  condicao_pagamento: string | null;
  forma_pagamento_id: string | null;
  forma_pagamento_nome: string | null;
  forma_pagamento_ativa: boolean;
  prazo_entrega_dias: number | null;
  pedido_minimo_centavos: number | null;
  observacao: string | null;
}

export const CAMINHO_FORNECIMENTO = (id: string) => `/cadastros/parceiros/${id}/fornecimento`;

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-tinta-3">{rotulo}</dt>
      <TextoCortado como="dd" className="min-w-0 text-right">
        {children}
      </TextoCortado>
    </div>
  );
}

/**
 * Cartão "Condições de compra" da ficha, no mesmo desenho do cartão Dados fiscais: leitura em linhas e edição num modal com
 * o `Formulario` do núcleo. Aparece para o fornecedor quando o Compras está instalado (as condições valem como padrão na
 * compra direta e no pedido); antes moravam só em Compras > Fornecedores, que continua editando o mesmo dado.
 */
export function CartaoFornecimento({ p, podeEditar }: { p: Ficha; podeEditar: boolean }) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const [editar, setEditar] = useState(false);
  const q = useQuery({ queryKey: ['cadastros', 'fornecimento', p.id], queryFn: () => get<Fornecimento>(CAMINHO_FORNECIMENTO(p.id)) });
  const formas = useQuery({
    queryKey: ['/cadastros/formas-pagamento', 'ativas'],
    queryFn: () => getTodos<{ id: string; nome: string; tipo?: string; abreviatura?: string | null }>('/cadastros/formas-pagamento', { ativa: true }),
    enabled: editar,
    staleTime: 30_000,
  });
  const f = q.data;
  const vazio = !f || (f.condicao_pagamento === null && f.forma_pagamento_id === null && f.prazo_entrega_dias === null && f.pedido_minimo_centavos === null && !f.observacao);
  const moeda = s.workspace.moeda;

  // A forma inativa escolhida antes segue na lista (a pessoa vê o que está gravado e troca se quiser).
  const opcoesForma = [
    ...(formas.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: rotuloForma(x) })),
    ...(f?.forma_pagamento_id && !f.forma_pagamento_ativa ? [{ valor: f.forma_pagamento_id, rotulo: t('fornecimento.formaInativa', { nome: rotuloNomeForma(f.forma_pagamento_nome) }) }] : []),
  ];
  const campos: DefCampo[] = [
    { nome: 'condicao_pagamento', rotulo: t('fornecimento.condicao'), tipo: 'texto', largura: 'meia', placeholder: '30/60/90', ajuda: t('fornecimento.condicaoAjuda') },
    { nome: 'forma_pagamento_id', rotulo: t('fornecimento.forma'), tipo: 'selecao', largura: 'meia', opcoes: opcoesForma },
    { nome: 'prazo_entrega_dias', rotulo: t('fornecimento.prazo'), tipo: 'numero', largura: 'meia', min: 0, max: 365, passo: 1 },
    { nome: 'pedido_minimo_centavos', rotulo: t('fornecimento.minimo'), tipo: 'dinheiro', largura: 'meia' },
    { nome: 'observacao', rotulo: t('fornecimento.observacao'), tipo: 'area' },
  ];

  async function salvar(v: Record<string, unknown>) {
    const numeroOuNulo = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : x === '' || x === null || x === undefined ? null : Number(x));
    await api('PUT', CAMINHO_FORNECIMENTO(p.id), {
      condicao_pagamento: String(v.condicao_pagamento ?? '').trim() || null,
      forma_pagamento_id: (v.forma_pagamento_id as string) || null,
      prazo_entrega_dias: numeroOuNulo(v.prazo_entrega_dias),
      pedido_minimo_centavos: numeroOuNulo(v.pedido_minimo_centavos),
      observacao: String(v.observacao ?? '').trim() || null,
    });
    await qc.invalidateQueries({ queryKey: ['cadastros', 'fornecimento', p.id] });
    await qc.invalidateQueries({ queryKey: ['chatter', 'cadastros.parceiro', p.id] });
    await qc.invalidateQueries({ queryKey: ['/compras/fornecedores'] });
    setEditar(false);
    toast.sucesso(tn('comum.salvo'));
  }

  return (
    <Cartao
      titulo={t('fornecimento.titulo')}
      acao={
        podeEditar &&
        q.isSuccess && (
          <Botao variante="fantasma" tamanho="sm" icone={vazio ? <Plus className="size-4" /> : <Pencil className="size-4" />} onClick={() => setEditar(true)}>
            {vazio ? t('fornecimento.definir') : t('fornecimento.editar')}
          </Botao>
        )
      }
    >
      <div data-fornecimento="">
        {q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : !f ? (
          <p className="text-sm text-tinta-3">{tn('comum.carregando')}</p>
        ) : vazio ? (
          <p className="text-sm text-tinta-3">{t('fornecimento.vazio')}</p>
        ) : (
          <dl className="grid gap-x-8 sm:grid-cols-2">
            <Linha rotulo={t('fornecimento.condicao')}>
              {!f.condicao_pagamento || f.condicao_pagamento === 'avista' ? t('fornecimento.avista') : <span className="numero">{f.condicao_pagamento}</span>}
            </Linha>
            {f.forma_pagamento_id && <Linha rotulo={t('fornecimento.forma')}>{f.forma_pagamento_ativa ? rotuloNomeForma(f.forma_pagamento_nome) : t('fornecimento.formaInativa', { nome: rotuloNomeForma(f.forma_pagamento_nome) })}</Linha>}
            {f.prazo_entrega_dias !== null && <Linha rotulo={t('fornecimento.prazo')}>{t('fornecimento.dias', { count: f.prazo_entrega_dias })}</Linha>}
            {f.pedido_minimo_centavos !== null && (
              <Linha rotulo={t('fornecimento.minimo')}>
                <span className="numero">{dinheiro(f.pedido_minimo_centavos, moeda)}</span>
              </Linha>
            )}
          </dl>
        )}
        {f?.observacao && <p className="mt-3 border-t border-borda pt-3 text-sm whitespace-pre-wrap text-tinta-2">{textoLivre(f.observacao, p.nome)}</p>}
        <p className="mt-3 text-xs text-tinta-3">{t('fornecimento.explicacao')}</p>
      </div>

      <Modal
        aberto={editar}
        aoFechar={() => setEditar(false)}
        titulo={t('fornecimento.titulo')}
        largura="lg"
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setEditar(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-fornecimento">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {editar && f && (
          <Formulario
            id="form-fornecimento"
            campos={campos}
            inicial={{
              condicao_pagamento: f.condicao_pagamento ?? '',
              forma_pagamento_id: f.forma_pagamento_id ?? '',
              prazo_entrega_dias: f.prazo_entrega_dias,
              pedido_minimo_centavos: f.pedido_minimo_centavos,
              observacao: f.observacao ?? '',
            }}
            aoEnviar={salvar}
          />
        )}
      </Modal>
    </Cartao>
  );
}
